const STORAGE_KEY = "kudavgorod-install-banner-dismissed";
const LEGACY_STORAGE_KEY = "kudgorod-install-banner-dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let installPromptCaptureStarted = false;
let installPromptInProgress = false;
const installPromptHandlers = new Set<InstallPromptHandler>();

export function isStandaloneMode(): boolean {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

export function isMobileOrTablet(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;

  return (
    navigator.maxTouchPoints > 1 &&
    window.matchMedia("(max-width: 1024px)").matches
  );
}

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isInstallBannerDismissed(): boolean {
  try {
    const dismissed =
      localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    return dismissed === "1";
  } catch {
    return false;
  }
}

export function dismissInstallBannerPermanently(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // ignore private mode / blocked storage
  }
}

export function shouldShowInstallBanner(): boolean {
  if (!isMobileOrTablet()) return false;
  if (isStandaloneMode()) return false;
  if (isInstallBannerDismissed()) return false;
  return true;
}

export type InstallPromptHandler = (event: BeforeInstallPromptEvent) => void;

function canUseCustomInstallPrompt(): boolean {
  return shouldShowInstallBanner() && !isIos() && !isInAppBrowser();
}

export function isInstallPromptInProgress(): boolean {
  return installPromptInProgress;
}

/** До React — Chrome может вызвать beforeinstallprompt рано. */
export function initInstallPromptCapture(): void {
  if (typeof window === "undefined" || installPromptCaptureStarted) {
    return;
  }

  installPromptCaptureStarted = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    if (!canUseCustomInstallPrompt()) {
      return;
    }

    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    installPromptHandlers.forEach((handler) => handler(deferredInstallPrompt!));
  });

  window.addEventListener("appinstalled", () => {
    installPromptInProgress = false;
    deferredInstallPrompt = null;
    dismissInstallBannerPermanently();
  });
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstallPrompt;
}

export function clearDeferredInstallPrompt(): void {
  deferredInstallPrompt = null;
}

export function subscribeToInstallPrompt(handler: InstallPromptHandler): () => void {
  installPromptHandlers.add(handler);
  if (deferredInstallPrompt) {
    handler(deferredInstallPrompt);
  }

  return () => {
    installPromptHandlers.delete(handler);
  };
}

/** prompt() нужно вызывать синхронно в обработчике клика, иначе жест теряется. */
export function runInstallPromptSync(
  event: BeforeInstallPromptEvent,
  onResult: (accepted: boolean) => void,
): void {
  if (installPromptInProgress) {
    return;
  }

  installPromptInProgress = true;

  try {
    event.prompt();
    event.userChoice
      .then(({ outcome }) => onResult(outcome === "accepted"))
      .catch(() => onResult(false))
      .finally(() => {
        installPromptInProgress = false;
      });
  } catch {
    installPromptInProgress = false;
    onResult(false);
  }
}

export function getInstallHint(): string {
  if (isIos()) {
    return getIosInstallBannerText();
  }

  if (isInAppBrowser()) {
    return getInAppCompactText();
  }

  if (isInstallPromptInProgress()) {
    return "Подтвердите установку в окне браузера и дождитесь завершения. Не открывайте меню параллельно.";
  }

  return getManualInstallIntro();
}

export type AndroidBrowserKind =
  | "chrome"
  | "samsung"
  | "yandex"
  | "miui"
  | "firefox"
  | "edge"
  | "other";

export function isXiaomiDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Miui|Redmi|XiaoMi|Mi Build|HM NOTE|MI \d/i.test(navigator.userAgent);
}

/** Старые Android (Redmi 4 и др.): Telegram часто без «Telegram» в UA, но с Version/4.0 WebView. */
export function isAndroidWebView(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  if (!/Android/i.test(ua)) return false;

  if (
    /MiuiBrowser|SamsungBrowser|YaBrowser|Firefox|EdgA/i.test(ua) &&
    !/\; wv\)/.test(ua)
  ) {
    return false;
  }

  if (/\; wv\)/.test(ua)) return true;
  if (/Version\/4\.0/i.test(ua) && /Chrome/i.test(ua)) return true;

  return false;
}

export function getAndroidBrowserKind(): AndroidBrowserKind {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent;
  if (/YaBrowser/i.test(ua)) return "yandex";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/MiuiBrowser/i.test(ua)) return "miui";
  if (/EdgA/i.test(ua)) return "edge";
  if (/Firefox/i.test(ua)) return "firefox";
  if (isXiaomiDevice() && !isAndroidWebView()) return "miui";
  if (/Chrome/i.test(ua) && !isAndroidWebView()) return "chrome";
  if (isXiaomiDevice()) return "miui";
  return "other";
}

export function getManualInstallIntro(): string {
  if (isIos()) {
    return getIosInstallBannerText();
  }

  return "Добавьте ярлык через меню браузера:";
}

export function getBrowserManualInstallSteps(): string[] {
  if (isIos()) {
    return getIosInstallSteps();
  }

  switch (getAndroidBrowserKind()) {
    case "yandex":
      return [
        "Нажмите меню (⋮) внизу или сверху экрана.",
        "Выберите «Добавить на главный экран».",
        "Подтвердите добавление.",
      ];
    case "samsung":
      return [
        "Нажмите меню (≡) внизу экрана.",
        "Выберите «Добавить страницу на» → «Главный экран».",
        "Нажмите «Добавить».",
      ];
    case "miui":
      return [
        "Нажмите «⋮» или «≡» в браузере.",
        "Выберите «Добавить ярлык на рабочий стол» или «Добавить на главный экран».",
        "Подтвердите добавление.",
      ];
    case "firefox":
      return [
        "Нажмите меню (⋮) в правом верхнем углу.",
        "Выберите «Установить» или «Добавить на главный экран».",
        "Подтвердите добавление.",
      ];
    case "edge":
      return [
        "Нажмите меню (⋯) внизу экрана.",
        "Выберите «Добавить на телефон» → «На главный экран».",
        "Подтвердите добавление.",
      ];
    case "chrome":
      return [
        "Нажмите меню (⋮) в правом верхнем углу.",
        "Выберите «Установить приложение» или «Добавить на главный экран».",
        "Подтвердите установку и дождитесь завершения.",
      ];
    default:
      return [
        "Откройте меню браузера (обычно ⋮ или ≡).",
        "Найдите пункт «Установить приложение» или «Добавить на главный экран».",
        "Подтвердите добавление ярлыка.",
      ];
  }
}

export function getInstallAcceptedIntro(): string {
  return "Если иконки нет на рабочем столе, добавьте вручную:";
}

export function isSafariBrowser(): boolean {
  if (!isIos()) return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
}

export function isChromeIos(): boolean {
  if (!isIos()) return false;
  return /CriOS/i.test(navigator.userAgent);
}

export function isIpad(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  if (/iPad/i.test(ua)) return true;

  return (
    /Macintosh/i.test(ua) &&
    navigator.maxTouchPoints > 1
  );
}

/** Telegram, VK, Instagram и др. — ярлык из встроенного браузера почти никогда не ставится. */
export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  if (
    /Telegram|FBAN|FBAV|Instagram|Line|Twitter|LinkedInApp|WhatsApp|Snapchat|TikTok|VKClient|OkApp/i.test(
      ua,
    )
  ) {
    return true;
  }

  if (/Android/i.test(ua) && /\; wv\)/.test(ua)) {
    return true;
  }

  if (isAndroidWebView()) {
    return true;
  }

  if (!isIos()) return false;

  // iOS: встроенный WebView без Safari/Chrome/Firefox в UA.
  if (
    !isSafariBrowser() &&
    !isChromeIos() &&
    !/FxiOS|EdgiOS|OPiOS/i.test(ua) &&
    /AppleWebKit/i.test(ua)
  ) {
    return true;
  }

  return false;
}

export function getInAppCompactText(): string {
  if (isIos()) {
    return "Откройте в Safari, чтобы сделать ярлык";
  }

  return "Откройте в браузере, чтобы сделать ярлык";
}

/** @deprecated use getInAppCompactText */
export function getIosInAppCompactText(): string {
  return getInAppCompactText();
}

export function getInAppInstallSteps(): string[] {
  if (isIos()) {
    return [
      "Нажмите «⋯» (три точки) в углу экрана.",
      "Выберите «Открыть в Safari» или «Open in external browser».",
      "В Safari: «Поделиться» → прокрутите вниз → «На экран Домой» → «Добавить».",
    ];
  }

  if (isXiaomiDevice()) {
    return [
      "В Telegram нажмите «⋮» (три точки) вверху.",
      "Выберите «Открыть в браузере» (не ищите «Chrome», если его нет).",
      "В браузере: «⋮» → «Добавить ярлык на рабочий стол» или «Добавить на главный экран».",
    ];
  }

  return [
    "В Telegram нажмите «⋮» (три точки) вверху.",
    "Выберите «Открыть в браузере» / «Open in browser».",
    "В браузере: «⋮» → «Добавить на главный экран» или «Добавить ярлык».",
  ];
}

export function getManualInstallStepsForCurrentBrowser(): string[] {
  if (isInAppBrowser() || isAndroidWebView()) {
    return getInAppInstallSteps();
  }

  return getBrowserManualInstallSteps();
}

export function getIosInstallBannerText(): string {
  if (isChromeIos()) {
    return "Добавьте ярлык через Chrome или откройте сайт в Safari:";
  }

  if (isSafariBrowser()) {
    return "Добавьте КудаВГород на главный экран через Safari:";
  }

  return "Откройте сайт в Safari (не во встроенном браузере):";
}

export function getIosInstallSteps(): string[] {
  if (isChromeIos()) {
    return [
      "Нажмите «⋯» справа в адресной строке Chrome.",
      "Выберите «Добавить на главный экран» (или «Поделиться» → «На экран Домой»).",
      "Подтвердите «Добавить».",
    ];
  }

  if (isSafariBrowser() && isIpad()) {
    return [
      "Нажмите «Поделиться» вверху (квадрат со стрелкой вверх).",
      "Прокрутите список действий вниз — пункт не всегда виден сразу.",
      "Выберите «На экран Домой» → «Добавить».",
    ];
  }

  if (isSafariBrowser()) {
    return [
      "Откройте сайт в обычном режиме Safari (не «Приватный»).",
      "Нажмите «Поделиться» внизу (квадрат со стрелкой вверх).",
      "Прокрутите список вниз → «На экран Домой» → «Добавить».",
    ];
  }

  return [
    "Скопируйте адрес сайта из адресной строки.",
    "Откройте Safari, вставьте адрес и перейдите на сайт.",
    "«Поделиться» → прокрутите вниз → «На экран Домой» → «Добавить».",
  ];
}

export function getIosInstallTroubleshootNote(): string | null {
  if (!isSafariBrowser() || isInAppBrowser()) {
    return null;
  }

  return "Нет пункта «На экран Домой»? Проверьте, что Safari не в режиме «Приватный», и прокрутите список «Поделиться» до конца.";
}

export function getInstallPendingHint(): string {
  return "Подтвердите установку в окне браузера. Не открывайте меню, пока установка не завершится.";
}
